import {
    Box,
    Button,
    DialogContent,
    TextField,
    DialogTitle,
    DialogActions,
    FormControl,
    InputLabel,
    MenuItem,
    FormHelperText,
} from '@mui/material';
import Select from '@mui/material/Select';
import colors from 'assets/theme/base/colors';
import pxToRem from 'assets/theme/function/px-to-rem';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { Controller, useForm } from 'react-hook-form';
import rgba from 'assets/theme/function/rgba';
import DialogIconClose from 'component/dialog-icon-close';
import SuccessSnackbarEffect from 'component/success-snackbar';
import { EditCategoryRequest } from 'types/toont-tint/toon-tint';
import {
    useEditCategoryToonTintMutation,
    useGetCategoryIdDataQuery,
} from 'store/slices/toon-tint/toon-tint';

interface Props {
    setOpenEditCategoryDialog: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: any;
    categoryId: string;
}

const defaultSelected = [
    { label: 'True', value: 'true' },
    { label: 'False', value: 'false' },
];

const EditCategory: React.FC<Props> = (props: Props) => {
    const { refetch, setOpenEditCategoryDialog, categoryId } = props;

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm<EditCategoryRequest>();

    const { data, isSuccess: dataIsSuccess } = useGetCategoryIdDataQuery(
        categoryId,
        {
            refetchOnMountOrArgChange: true,
        },
    );

    const [editCard, { error, isError, isSuccess, isLoading }] =
        useEditCategoryToonTintMutation();

    const formRef = useRef<HTMLFormElement>(null);

    const dispatch = useAppDispatch();

    const onSubmit = (values: EditCategoryRequest) => {
        editCard({
            categoryPosition: Number(values.categoryPosition),
            itemZIndex: Number(values.itemZIndex),
            defaultSelected:
                values.defaultSelected === 'true'
                    ? true
                    : values.defaultSelected === 'false'
                      ? false
                      : '',
            isVisible:
                values.isVisible === 'true'
                    ? true
                    : values.isVisible === 'false'
                      ? false
                      : '',
            categoryId: categoryId,
        });
    };

    const closeModal = () => {
        setOpenEditCategoryDialog(false);
        reset();
    };

    useEffect(() => {
        if (dataIsSuccess && data) {
            reset({
                categoryPosition: Number(data.categoryPosition) ?? '',
                defaultSelected: data.defaultSelected?.toString() ?? '',
                itemZIndex: data.itemZIndex ?? '',
                isVisible:
                    data.isVisible !== undefined
                        ? data.isVisible.toString()
                        : '',
            });
        }
    }, [dataIsSuccess, data, reset]);

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [isError]);

    useEffect(() => {
        if (isSuccess) {
            setOpenEditCategoryDialog(false);
            refetch();
        }
    }, [isSuccess]);

    return (
        <>
            <Box
                display={'flex'}
                justifyContent={'center'}
                bgcolor={rgba(colors.primary.main, 0.5)}
            >
                <DialogTitle textTransform={'uppercase'}>
                    EDIT CATEGORY
                </DialogTitle>
                <DialogIconClose handleClose={closeModal} />
            </Box>

            <DialogContent>
                {dataIsSuccess && (
                    <Box
                        ref={formRef}
                        component="form"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <Box display={'flex'} pt={pxToRem(15)}>
                            <Controller
                                name="categoryPosition"
                                control={control}
                                rules={{
                                    required: 'Position Category is required',
                                }}
                                render={({
                                    field: { name, value = '', onChange },
                                }) => (
                                    <TextField
                                        fullWidth
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        label="Position Category"
                                        error={Boolean(errors[name])}
                                        helperText={
                                            errors[name]?.message?.toString() ||
                                            ''
                                        }
                                    />
                                )}
                            />

                            <Controller
                                name="itemZIndex"
                                control={control}
                                rules={{
                                    required: 'Z Index Category is required',
                                }}
                                render={({
                                    field: { name, value, onChange },
                                }) => (
                                    <TextField
                                        sx={{ ml: pxToRem(10) }}
                                        fullWidth
                                        name={name}
                                        value={value ?? 0}
                                        onChange={(e) =>
                                            onChange(Number(e.target.value))
                                        }
                                        label="Item Z_Index"
                                        error={Boolean(errors[name])}
                                        helperText={
                                            errors[name]?.message?.toString() ||
                                            ''
                                        }
                                    />
                                )}
                            />

                            <FormControl
                                fullWidth
                                sx={{ ml: pxToRem(10) }}
                                error={Boolean(errors?.defaultSelected)}
                            >
                                <InputLabel>Default Selected </InputLabel>
                                <Controller
                                    name="defaultSelected"
                                    control={control}
                                    render={({
                                        field: { name, value, onChange },
                                    }) => (
                                        <Select
                                            label="Default Selected "
                                            name={name}
                                            value={value || ''}
                                            onChange={onChange}
                                            inputProps={{
                                                'aria-label': 'CardCategory',
                                            }}
                                        >
                                            {defaultSelected?.map((status) => (
                                                <MenuItem
                                                    key={status.toString()}
                                                    value={status.value.toString()}
                                                >
                                                    {status.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                {errors?.defaultSelected && (
                                    <FormHelperText>
                                        {errors?.defaultSelected.message?.toString()}
                                    </FormHelperText>
                                )}
                            </FormControl>

                            <FormControl
                                error={Boolean(errors?.isVisible)}
                                fullWidth
                                sx={{ ml: pxToRem(10) }}
                            >
                                <InputLabel>Is Visible</InputLabel>
                                <Controller
                                    name="isVisible"
                                    control={control}
                                    rules={{
                                        required: 'Is required',
                                    }}
                                    render={({
                                        field: { name, value, onChange },
                                    }) => (
                                        <Select
                                            label="Selected Default"
                                            name={name}
                                            value={value || ''}
                                            onChange={onChange}
                                        >
                                            {defaultSelected?.map((status) => (
                                                <MenuItem
                                                    key={status.toString()}
                                                    value={status.value.toString()}
                                                >
                                                    {status.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                {errors?.isVisible && (
                                    <FormHelperText>
                                        {errors?.isVisible.message?.toString()}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    sx={{ width: '50%' }}
                    onClick={() => formRef.current?.requestSubmit()}
                    variant={'contained'}
                    loading={isLoading}
                >
                    Edit Category
                </Button>
                <Button
                    sx={{ width: '50%', bgcolor: colors.action.menuSelect }}
                    onClick={() => closeModal()}
                    variant={'contained'}
                >
                    Cancel
                </Button>
            </DialogActions>

            <SuccessSnackbarEffect
                isSuccess={isSuccess}
                message={'Successfully Edit an Card!'}
            />
        </>
    );
};
export default EditCategory;
